/**
 * Stok Referans Bütünlüğü — orphan tespit paneli (salt-okunur)
 *
 * stok_hareketleri yumuşak-FK (referans_tipi/referans_id) bütünlük raporu.
 * Backend: GET /api/stok/referans-butunlugu (MANAGE_SETTINGS). Anayasa M.5 çift katman:
 * backend require_permission + frontend hasPermission gating.
 *
 * Aksiyon YOK (salt rapor) — tarihsel orphan'lar M.6 gereği panelden vaka-bazlı
 * değerlendirilir; bu sayfa otomatik silme/değiştirme yapmaz.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const OzetKart = ({ etiket, deger, vurgu }) => (
  <div
    style={{
      flex: 1,
      minWidth: 140,
      background: '#fff',
      border: `1px solid ${colors.border}`,
      borderRadius: radius.md,
      padding: '14px 16px',
    }}
  >
    <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>{etiket}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: vurgu ? '#c62828' : colors.textPrimary }}>
      {deger}
    </div>
  </div>
);

const StokReferansButunluguPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canView = hasPermission(
    user?.role,
    PERMISSIONS.MANAGE_SETTINGS,
    user?.effectivePermissions,
  );

  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRapor = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stok/referans-butunlugu`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRapor(data);
    } catch (err) {
      logError('Referans bütünlüğü yüklenemedi:', err);
      toast.error('Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) fetchRapor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  if (!canView) {
    return (
      <div>
        <Header title="Stok Referans Bütünlüğü" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Bu sayfayı görüntüleme yetkiniz yok. (MANAGE_SETTINGS gerekli)
        </div>
      </div>
    );
  }

  if (loading && !rapor) {
    return (
      <div>
        <Header title="Stok Referans Bütünlüğü" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Yükleniyor…
        </div>
      </div>
    );
  }

  const tipler = rapor?.tipler || [];
  const bilinmeyen = rapor?.bilinmeyen_tipler || [];
  const ozet = rapor?.ozet || {};

  const th = { padding: 10, textAlign: 'left', fontSize: 12, color: colors.textSecondary };
  const td = { padding: 10, fontSize: 13, borderTop: `1px solid ${colors.border}` };
  const sayiTd = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  const gitDetay = (tip, sinif) =>
    navigate(`/ayarlar/stok-referans-butunlugu/detay?tip=${encodeURIComponent(tip)}&sinif=${sinif}`);

  // Sıfırdan büyük sayım → detay sayfasına tıklanabilir link; 0 → düz metin.
  // (Fonksiyon olarak çağrılır, <Component/> değil — no-unstable-nested-components'i tetiklemez.)
  const sayiHucre = (t, sinif, deger, vurgu) => {
    const renk = vurgu && deger > 0 ? '#c62828' : colors.textPrimary;
    if (!deger) return <td style={{ ...sayiTd, color: renk }}>0</td>;
    return (
      <td style={sayiTd}>
        <button
          onClick={() => gitDetay(t.referans_tipi, sinif)}
          title={`${t.referans_tipi} — ${sinif} satırlarını gör`}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: renk,
            fontWeight: vurgu ? 700 : 500,
            textDecoration: 'underline',
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {deger.toLocaleString('tr-TR')}
        </button>
      </td>
    );
  };

  return (
    <div>
      <Header title="Stok Referans Bütünlüğü" />

      <div
        style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}
      >
        <div
          style={{
            background: '#eef6ff',
            border: '1px solid #b6daff',
            color: '#0b5394',
            padding: '12px 16px',
            borderRadius: radius.md,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Her stok hareketi bir kaynak belgeyi işaret eder (<strong>referans_tipi</strong> →{' '}
          <strong>referans_id</strong>). Bu rapor yumuşak-FK bütünlüğünü denetler:{' '}
          <strong>orphan</strong> = kaynak belgesi silinmiş hareket;{' '}
          <strong>sentinel</strong> = kaynak-belgesiz (referans_id=0) meşru hareket;{' '}
          <strong>legacy</strong> = eski sistemden gelen referans_id=0 (ZAYI/SAYIM). Salt-okunur.
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <OzetKart etiket="Toplam hareket" deger={(ozet.toplam || 0).toLocaleString('tr-TR')} />
          <OzetKart etiket="Orphan (kaynak silinmiş)" deger={ozet.orphan || 0} vurgu={ozet.orphan > 0} />
          <OzetKart etiket="Geçersiz (negatif)" deger={ozet.gecersiz || 0} vurgu={ozet.gecersiz > 0} />
          <OzetKart etiket="Bilinmeyen tip" deger={ozet.bilinmeyen || 0} vurgu={ozet.bilinmeyen > 0} />
          <button
            onClick={fetchRapor}
            style={{
              alignSelf: 'center',
              padding: '10px 16px',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            ↻ Yenile
          </button>
        </div>

        <div style={{ background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: colors.background }}>
              <tr>
                <th style={th}>referans_tipi</th>
                <th style={th}>hedef tablo</th>
                <th style={{ ...th, textAlign: 'right' }}>toplam</th>
                <th style={{ ...th, textAlign: 'right' }}>bağlı</th>
                <th style={{ ...th, textAlign: 'right' }}>orphan</th>
                <th style={{ ...th, textAlign: 'right' }}>sentinel</th>
                <th style={{ ...th, textAlign: 'right' }}>legacy</th>
                <th style={{ ...th, textAlign: 'right' }}>geçersiz</th>
              </tr>
            </thead>
            <tbody>
              {tipler.map((t) => (
                <tr key={t.referans_tipi}>
                  <td style={{ ...td, fontWeight: 600 }}>{t.referans_tipi}</td>
                  <td style={{ ...td, color: colors.textSecondary }}>{t.hedef_tablo || '—'}</td>
                  <td style={sayiTd}>{(t.toplam || 0).toLocaleString('tr-TR')}</td>
                  <td style={sayiTd}>{(t.bagli || 0).toLocaleString('tr-TR')}</td>
                  {sayiHucre(t, 'orphan', t.orphan || 0, true)}
                  {sayiHucre(t, 'sentinel', t.sentinel_sifir || 0, false)}
                  {sayiHucre(t, 'legacy', t.legacy_sifir || 0, false)}
                  {sayiHucre(t, 'gecersiz', t.gecersiz || 0, true)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {bilinmeyen.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 14, color: '#c62828' }}>
              ⚠️ Bilinmeyen referans_tipi (registry dışı — yazım drift'i olabilir)
            </h3>
            <div style={{ background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden', maxWidth: 360 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: colors.background }}>
                  <tr>
                    <th style={th}>referans_tipi</th>
                    <th style={{ ...th, textAlign: 'right' }}>adet</th>
                  </tr>
                </thead>
                <tbody>
                  {bilinmeyen.map((b) => (
                    <tr key={b.referans_tipi}>
                      <td style={td}>{b.referans_tipi}</td>
                      <td style={sayiTd}>{b.adet}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StokReferansButunluguPage;
